import React from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
} from "recharts";
import styles from "./financialSummary.module.scss";

interface FinancialSummaryProps {
  data?: {
    timeRange: string;
    totalContractCosts: number;
    totalContractRevenues: number;
    netRevenue: number;
  };
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({ data }) => {
  const costs = data?.totalContractCosts ?? 200000;
  const revenues = data?.totalContractRevenues ?? 120000;
  const netRevenue = data?.netRevenue ?? 80000;

  const chartData = [
    {
      name: "Financial Summary",
      costs: costs,
      revenues: revenues,
      netRevenue: netRevenue,
    },
  ];

  const metricsData = [
    {
      value: costs,
      label: "Total Contract Costs",
      color: "#009b7e",
    },
    {
      value: revenues,
      label: "Total Contract Revenues",
      color: "#acebdf",
    },
    {
      value: netRevenue,
      label: "Net Revenue",
      color: "url(#stripePattern)",
      isStriped: true,
    },
  ];

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            Sum of costs and revenues
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Track total contract costs and revenues.
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <FormControl className={styles.filter}>
            <Select
              defaultValue="Last quarter"
              IconComponent={KeyboardArrowDownIcon}
              className={styles.select}
            >
              <MenuItem value="Last quarter">Last quarter</MenuItem>
            </Select>
          </FormControl>
          <IconButton className={styles.moreButton} aria-label="More options">
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={40}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <pattern
                id="stripePattern"
                patternUnits="userSpaceOnUse"
                width="8"
                height="8"
                patternTransform="rotate(45)"
              >
                <rect width="4" height="8" fill="#e1f8f4" />
                <rect x="4" width="4" height="8" fill="#d4f3ec" />
              </pattern>
            </defs>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" hide />
            <Bar dataKey="costs" stackId="a" fill="#009b7e" radius={[4, 0, 0, 4]} />
            <Bar dataKey="revenues" stackId="a" fill="#acebdf" />
            <Bar dataKey="netRevenue" stackId="a" fill="url(#stripePattern)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box className={styles.metrics}>
        {metricsData.map((metric, index) => (
          <Box key={index} className={styles.metricItem}>
            <Typography variant="h6" className={styles.metricValue}>
              ${(metric.value / 1000).toFixed(0)}K
            </Typography>
            <Box className={styles.metricLabelContainer}>
              <Box
                className={`${styles.colorSwatch} ${
                  metric.isStriped ? styles.stripedSwatch : ""
                }`}
                style={
                  !metric.isStriped ? { backgroundColor: metric.color } : {}
                }
              />
              <Typography variant="body2" className={styles.metricLabel}>
                {metric.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FinancialSummary;
